import React from "react";
import {
  CheckCircle,
  Calendar,
  Weight,
  Ruler,
  Palette,
  User,
  Tag,
  Heart,
} from "lucide-react";
import { UiButton } from "@/components";
import { format } from "date-fns";
import { fr } from "date-fns/locale";

interface AnimalSuccessCardProps {
  animal:
    | {
        // Type simple (enrôlement unique)
        id?: string;
        nom?: string;
        type?: string;
        animalType?: string;
        sexe?: string;
        espece?: string;
        poids?: number;
        taille?: number;
        couleur?: string;
        dateNaissance?: string;
        laitiere?: boolean;
        matricule?: string;
        proprietaire?: {
          nom?: string;
          prenom?: string;
          matricule?: string;
        };
        dateEnrolement?: string;
      }
    | {
        // Type campagne (enrôlement en lot)
        nomCampagne?: string;
        referenceCommande?: string;
        referenceLot?: string;
        regionReference?: string;
        villeReference?: string;
        proprietaire?: {
          nom?: string;
          prenom?: string;
          phone?: string;
          email?: string;
          dateCreation?: string;
          derniereConnexion?: string;
          dateNaissance?: string;
          dateMiseAJour?: string;
          adresse?: {
            pays?: string;
            cp?: string;
            ville?: string;
            intituleRue?: string;
            numeroRue?: number;
            adresseComplete?: string;
          };
          matricule?: string;
          villeNaissance?: string;
          villeActivite?: string;
          type?: string;
          disabled?: boolean;
        };
        type?: string;
        espece?: string;
        sexe?: string;
        premiereBoucle?: string;
        dernierBoucle?: string;
        matricules?: string[];
        guids?: string[];
      };
  onDownloadCertificate?: () => void;
  onShare?: () => void;
  onSaveAgain?: () => void;
}

export const AnimalSuccessCard: React.FC<AnimalSuccessCardProps> = ({
  animal,
  onSaveAgain,
}) => {
  // Fonction pour vérifier le type de données et normaliser
  const isLotEnrollment = (data: any): boolean => {
    return "nomCampagne" in data || "matricules" in data || "guids" in data;
  };

  // Fonction pour normaliser les données selon le type
  const normalizeAnimalData = (data: any) => {
    if (isLotEnrollment(data)) {
      // Format campagne/lot
      return {
        nom: data.nomCampagne || "Enrôlement en lot",
        type: data.type,
        animalType: data.type,
        sexe: data.sexe,
        espece: data.espece,
        matricule: data.matricules?.[0] || data.premiereBoucle || "N/A",
        matricules: data.matricules || [],
        guids: data.guids || [],
        premiereBoucle: data.premiereBoucle,
        dernierBoucle: data.dernierBoucle,
        referenceLot: data.referenceLot,
        referenceCommande: data.referenceCommande,
        nomCampagne: data.nomCampagne,
        regionReference: data.regionReference,
        villeReference: data.villeReference,
        proprietaire: data.proprietaire,
        isLotEnrollment: true,
      };
    } else {
      // Format simple
      return {
        ...data,
        isLotEnrollment: false,
      };
    }
  };

  const normalizedAnimal = normalizeAnimalData(animal);

  const getAnimalIcon = (type: string) => {
    switch (type?.toUpperCase()) {
      case "BOVIN":
        return "🐄";
      case "CAPRIN":
        return "🐐";
      case "OVIN":
        return "🐑";
      case "PORCIN":
        return "🐷";
      case "VOLAILLE":
        return "🐔";
      default:
        return "🐾";
    }
  };

  const getSexeDisplay = (sexe: string) => {
    switch (sexe?.toUpperCase()) {
      case "M":
        return { label: "Mâle", icon: "♂️", color: "text-blue-600" };
      case "F":
        return { label: "Femelle", icon: "♀️", color: "text-pink-600" };
      default:
        return { label: "Non spécifié", icon: "⚪", color: "text-gray-600" };
    }
  };

  const sexeInfo = getSexeDisplay(normalizedAnimal.sexe || "");

  return (
    <div className="w-full max-w-4xl mx-auto">
      {/* Header de succès avec animation */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 rounded-t-2xl p-6 text-white">
        <div className="flex items-center justify-center mb-4">
          <div className="relative">
            <CheckCircle className="w-16 h-16 animate-bounce" />
            <div className="absolute -top-1 -right-1 w-4 h-4 bg-white rounded-full animate-ping"></div>
          </div>
        </div>
        <h2 className="text-3xl font-bold text-center mb-2">
          {normalizedAnimal.isLotEnrollment
            ? "Enrôlement en Lot Réussi !"
            : "Enrôlement Réussi !"}
        </h2>
        <p className="text-center text-green-100 text-lg">
          {normalizedAnimal.isLotEnrollment
            ? `${
                normalizedAnimal.matricules?.length || 0
              } animaux ont été enregistrés avec succès`
            : "Votre animal a été enregistré avec succès dans notre système"}
        </p>
      </div>

      {/* Carte principale */}
      <div className="bg-white rounded-b-2xl shadow-2xl overflow-hidden">
        {/* Section informations principales */}
        <div className="p-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Colonne 1: Avatar et infos principales */}
            <div className="lg:col-span-1">
              <div className="text-center">
                <div className="w-32 h-32 mx-auto bg-gradient-to-br from-blue-100 to-indigo-200 rounded-full flex items-center justify-center text-6xl mb-4 shadow-lg">
                  {getAnimalIcon(
                    normalizedAnimal.animalType || normalizedAnimal.type || ""
                  )}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-2">
                  {normalizedAnimal.nom || "Animal sans nom"}
                </h3>
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span className="text-lg">{sexeInfo.icon}</span>
                  <span className={`font-medium ${sexeInfo.color}`}>
                    {sexeInfo.label}
                  </span>
                </div>
                <div className="bg-gray-100 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Matricule</p>
                  <p className="font-mono font-bold text-lg text-gray-800">
                    {normalizedAnimal.matricule || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Colonne 2: Détails de l'animal */}
            <div className="lg:col-span-2">
              <h4 className="text-xl font-semibold text-gray-800 mb-6 border-b border-gray-200 pb-2">
                Informations de l'animal
              </h4>

              <div className="grid grid-cols-1 gap-6">
                {/* Type et espèce */}
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                    <Tag className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm text-blue-600 font-medium">Type</p>
                      <p className="font-semibold text-gray-800">
                        {normalizedAnimal.type ||
                          normalizedAnimal.animalType ||
                          "Non spécifié"}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg">
                    <div className="w-5 h-5 text-purple-600 font-bold">🧬</div>
                    <div>
                      <p className="text-sm text-purple-600 font-medium">
                        Espèce
                      </p>
                      <p className="font-semibold text-gray-800">
                        {normalizedAnimal.espece || "Non spécifiée"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Informations spécifiques selon le type d'enrôlement */}
                {normalizedAnimal.isLotEnrollment ? (
                  <div className="space-y-4">
                    {/* Informations de lot */}
                    {normalizedAnimal.nomCampagne && (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Tag className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-600 font-medium">
                            Campagne
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.nomCampagne}
                          </p>
                        </div>
                      </div>
                    )}

                    {normalizedAnimal.referenceLot && (
                      <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                        <Tag className="w-5 h-5 text-yellow-600" />
                        <div>
                          <p className="text-sm text-yellow-600 font-medium">
                            Référence du lot
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.referenceLot}
                          </p>
                        </div>
                      </div>
                    )}

                    {(normalizedAnimal.premiereBoucle ||
                      normalizedAnimal.dernierBoucle) && (
                      <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                        <Tag className="w-5 h-5 text-indigo-600" />
                        <div>
                          <p className="text-sm text-indigo-600 font-medium">
                            Boucles
                          </p>
                          <p className="font-semibold text-gray-800">
                            De {normalizedAnimal.premiereBoucle} à{" "}
                            {normalizedAnimal.dernierBoucle}
                          </p>
                        </div>
                      </div>
                    )}

                    {normalizedAnimal.matricules &&
                      normalizedAnimal.matricules.length > 0 && (
                        <div className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 font-medium mb-2">
                            Matricules générés (
                            {normalizedAnimal.matricules.length})
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {normalizedAnimal.matricules
                              .slice(0, 5)
                              .map((matricule: string, index: number) => (
                                <span
                                  key={index}
                                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-mono rounded"
                                >
                                  {matricule}
                                </span>
                              ))}
                            {normalizedAnimal.matricules.length > 5 && (
                              <span className="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded">
                                +{normalizedAnimal.matricules.length - 5} autres
                              </span>
                            )}
                          </div>
                        </div>
                      )}

                    {(normalizedAnimal.regionReference ||
                      normalizedAnimal.villeReference) && (
                      <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                        <div className="w-5 h-5 text-orange-600">📍</div>
                        <div>
                          <p className="text-sm text-orange-600 font-medium">
                            Localisation
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.villeReference},{" "}
                            {normalizedAnimal.regionReference}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Caractéristiques physiques pour enrôlement simple */}
                    {normalizedAnimal.poids !== 0 && (
                      <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
                        <Weight className="w-5 h-5 text-orange-600" />
                        <div>
                          <p className="text-sm text-orange-600 font-medium">
                            Poids
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.poids} kg
                          </p>
                        </div>
                      </div>
                    )}

                    {normalizedAnimal.taille !== 0 && (
                      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
                        <Ruler className="w-5 h-5 text-green-600" />
                        <div>
                          <p className="text-sm text-green-600 font-medium">
                            Taille
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.taille} cm
                          </p>
                        </div>
                      </div>
                    )}

                    {normalizedAnimal.couleur && (
                      <div className="flex items-center gap-3 p-3 bg-pink-50 rounded-lg">
                        <Palette className="w-5 h-5 text-pink-600" />
                        <div>
                          <p className="text-sm text-pink-600 font-medium">
                            Couleur
                          </p>
                          <p className="font-semibold text-gray-800">
                            {normalizedAnimal.couleur}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Informations supplémentaires */}
              <div className="mt-6 grid grid-cols-1  gap-4">
                {normalizedAnimal.dateNaissance && (
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-sm text-yellow-600 font-medium">
                        Date de naissance
                      </p>
                      <p className="font-semibold text-gray-800">
                        {format(
                          new Date(normalizedAnimal.dateNaissance),
                          "dd MMMM yyyy",
                          { locale: fr }
                        )}
                      </p>
                    </div>
                  </div>
                )}

                {normalizedAnimal.laitiere !== undefined && (
                  <div className="flex items-center gap-3 p-3 bg-indigo-50 rounded-lg">
                    <Heart className="w-5 h-5 text-indigo-600" />
                    <div>
                      <p className="text-sm text-indigo-600 font-medium">
                        Laitière
                      </p>
                      <p className="font-semibold text-gray-800">
                        {normalizedAnimal.laitiere ? "Oui" : "Non"}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Section propriétaire */}
          {normalizedAnimal.proprietaire && (
            <div className="mt-8 p-6 bg-gray-50 rounded-xl">
              <h4 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" />
                Propriétaire
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Nom complet</p>
                  <p className="font-semibold text-gray-800">
                    {normalizedAnimal.proprietaire.prenom}{" "}
                    {normalizedAnimal.proprietaire.nom}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Matricule</p>
                  <p className="font-mono font-semibold text-gray-800">
                    {normalizedAnimal.proprietaire.matricule}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Date d'enrôlement</p>
                  <p className="font-semibold text-gray-800">
                    {normalizedAnimal.dateEnrolement
                      ? format(
                          new Date(normalizedAnimal.dateEnrolement),
                          "dd/MM/yyyy",
                          {
                            locale: fr,
                          }
                        )
                      : format(new Date(), "dd/MM/yyyy", {
                          locale: fr,
                        })}
                  </p>
                </div>
              </div>
              {/* Informations supplémentaires pour les enrôlements de lot */}
              {normalizedAnimal.isLotEnrollment &&
                normalizedAnimal.proprietaire && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    {normalizedAnimal.proprietaire.email && (
                      <div>
                        <p className="text-sm text-gray-600">Email</p>
                        <p className="font-semibold text-gray-800">
                          {normalizedAnimal.proprietaire.email}
                        </p>
                      </div>
                    )}
                    {normalizedAnimal.proprietaire.phone && (
                      <div>
                        <p className="text-sm text-gray-600">Téléphone</p>
                        <p className="font-semibold text-gray-800">
                          {normalizedAnimal.proprietaire.phone}
                        </p>
                      </div>
                    )}
                    {normalizedAnimal.proprietaire.adresse?.adresseComplete && (
                      <div className="md:col-span-2">
                        <p className="text-sm text-gray-600">Adresse</p>
                        <p className="font-semibold text-gray-800">
                          {
                            normalizedAnimal.proprietaire.adresse
                              .adresseComplete
                          }
                        </p>
                      </div>
                    )}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="px-8 py-6 bg-gray-50 border-t border-gray-200">
          <div className="flex flex-wrap gap-4 justify-center md:justify-end">
            {onSaveAgain && (
              <UiButton
                onClick={onSaveAgain}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Faire un autre enrolement
              </UiButton>
            )}
          </div>
        </div>
      </div>

      {/* Message de confirmation */}
      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <p className="text-blue-800 text-center">
          <strong>Félicitations !</strong>
          {normalizedAnimal.isLotEnrollment
            ? ` ${
                normalizedAnimal.matricules?.length || 0
              } animaux ont été enregistrés avec succès.`
            : " L'animal a été enregistré avec succès."}
        </p>
      </div>
    </div>
  );
};

export default AnimalSuccessCard;
